/*
    render multi curves for multi logs.
    support render with or without Y axis.

    Per-curve display (color / line style / width / scale / reverse / min-max) comes
    from the professional template in app/logViewerDefaults.js, resolved by mnemonic
    (with alias mapping), and overridable per user via settings.curveOverrides.
*/
import React, { useMemo, useRef } from "react";
import ReactECharts from "echarts-for-react";
import {
    selectFontColorSetting, selectFontSettings, selectLogListForNewLog,
    selectTopNameList, selectTopsSelected, selectCurveOverrides, selectChartSettings,
    selectBackgroundColorSetting,
} from "features/petrophysics/petrophysicsSlice";
import { useSelector } from "react-redux";
import { COLORS, FONT_SIZE, getColorIndexFromTopNamesList } from "app/codes";
import { getCurveDefault, lineDash } from "app/logViewerDefaults";

const PpWellMultiCurve = (props) => {
    const { uwi, param, titleTooltip, tops, zoomRef, height, onTopCoordsChange, onChartReady, index, showZoomer, startEndValue, syncDepth } = props;
    const { dataSeries, yAxisboundary, config } = props;
    const [yAxisMin, yAxisMax] = yAxisboundary;
    const { multiYAxis, showYAxis, columnWidth } = config || { multiYAxis: false };

    const topNamesList = useSelector(selectTopNameList);
    const selectedTop = useSelector(selectTopsSelected);
    const fontColor = useSelector(selectFontColorSetting);
    const backgroundColor = useSelector(selectBackgroundColorSetting);
    const fontSize = useSelector(selectFontSettings)['curve'];
    const wellFontSize = useSelector(selectFontSettings)['well'];
    const labelFontSize = useSelector(selectFontSettings)['scale'];
    const logListForNewLog = useSelector(selectLogListForNewLog);
    const curveOverrides = useSelector(selectCurveOverrides);
    const chartSettings = useSelector(selectChartSettings);
    const insRef = useRef();

    const showGrid = chartSettings ? chartSettings.showGrid : true;
    const gridColor = (chartSettings && chartSettings.gridColor) || "#888";

    const getLineColor = (key) => (getColorIndexFromTopNamesList(topNamesList, key) !== -1)
        ? COLORS[getColorIndexFromTopNamesList(topNamesList, key) + 2]
        : 'rgba(0, 0, 0, 0)';

    // resolve the professional default (+ user override) for one logId "logType:logName"
    const curveConfigFor = (logId) => {
        const logName = logId.split(":")[1] || logId;
        return getCurveDefault(logName, curveOverrides);
    };

    // normalize template min/max (which may be min>max for reverse curves) into
    // echarts (lo, hi, inverse). undefined -> fall back to data unit range / auto.
    const axisRange = (cfg, unitMin, unitMax) => {
        let lo = cfg.min, hi = cfg.max;
        if (lo === undefined || lo === null) lo = (unitMin === undefined || unitMin === null) ? 'dataMin' : unitMin;
        if (hi === undefined || hi === null) hi = (unitMax === undefined || unitMax === null) ? 'dataMax' : unitMax;
        const inverse = !!cfg.reverse;
        if (typeof lo === 'number' && typeof hi === 'number' && lo > hi) {
            return { min: hi, max: lo, inverse: true };
        }
        return { min: lo, max: hi, inverse };
    };

    const buildXAxis = () => {
        if (!dataSeries) return [];
        return Object.keys(dataSeries).map((logId, i) => {
            const [, logName] = logId.split(":");
            const [, , unitMin, unitMax] = dataSeries[logId][0] || [];
            const cfg = curveConfigFor(logId);
            const { min, max, inverse } = axisRange(cfg, unitMin, unitMax);
            const unitLabel = (logListForNewLog && logListForNewLog[logId])
                ? ' (' + logListForNewLog[logId].log_unit + ')'
                : (cfg.unit ? ' (' + cfg.unit + ')' : '');
            return {
                type: cfg.scale === "log" ? "log" : "value",
                id: i,
                position: 'top',
                name: `${logName}${unitLabel}`,
                nameLocation: 'middle',
                min,
                max,
                inverse,
                offset: 15 * i,
                nameGap: 0,
                splitLine: {
                    show: showGrid && i === 0,
                    lineStyle: { opacity: 0.6, color: gridColor },
                },
                axisLine: { lineStyle: { color: cfg.color } },
                axisPointer: { show: true, label: { show: true, precision: 0 }, snap: true },
                axisTick: { show: false },
                axisLabel: {
                    show: i === 0,
                    color: fontColor,
                    inside: true,
                    rotate: 90,
                    position: 'top',
                    fontSize: FONT_SIZE[labelFontSize],
                    lineHeight: 100,
                    height: 50,
                    hideOverlap: false,
                },
                nameTextStyle: {
                    color: i === 0 ? fontColor : cfg.color,
                    textBorderColor: 'grey',
                    textBorderWidth: 1,
                    fontSize: FONT_SIZE[fontSize],
                },
            };
        });
    };

    const buildSeries = () => {
        if (!dataSeries) return [];
        return Object.keys(dataSeries).map((logId, i) => {
            const [, logName] = logId.split(":");
            const cfg = curveConfigFor(logId);
            return {
                name: logName,
                type: 'line',
                smooth: false,
                sampling: 'lttb',         // downsample dense log data for performance
                large: true,
                largeThreshold: 2000,
                progressive: 4000,
                data: dataSeries[logId].map(item => [item[1], item[0]]),
                symbol: 'none',
                symbolSize: 0,
                yAxisIndex: multiYAxis ? i : 0,
                xAxisIndex: i,
                lineStyle: {
                    color: cfg.color,
                    width: cfg.width || 1,
                    type: lineDash(cfg.style),
                },
                itemStyle: { color: cfg.color, borderWidth: 0 },
                markLine: {
                    silent: true,
                    symbol: 'none',
                    lineStyle: { color: '#0f0', type: 'solid' },
                    data: tops && selectedTop && selectedTop.map((key) => ({
                        yAxis: tops[key] || '',
                        lineStyle: { color: getLineColor(key) },
                        label: {
                            position: 'start',
                            formatter: `${key}`,
                            color: getLineColor(key),
                            overflow: 'truncate',
                            width: 40,
                        },
                    })),
                },
            };
        });
    };

    const buildYAxis = () => ({
        type: "value",
        offset: 0,
        position: "left",
        inverse: true,
        show: true,
        min: yAxisMin ?? 'dataMin',
        max: yAxisMax ?? 'dataMax',
        axisPointer: { show: true, label: { show: index === 0, precision: 0 }, snap: true },
        axisLine: { onZero: false, show: true, lineStyle: { color: "#aaa" } },
        splitLine: { show: showGrid, lineStyle: { opacity: 0.2, color: gridColor } },
        axisTick: { show: showYAxis },
        axisLabel: { show: showYAxis, interval: 'auto', color: fontColor, fontSize: FONT_SIZE[labelFontSize] },
        name: index === 0 ? uwi : '',
        nameLocation: 'start',
        nameGap: parseInt(33 * (parseInt(height) / 440)),
        nameTextStyle: { color: fontColor, padding: [0, 0, 0, -13], fontSize: FONT_SIZE[wellFontSize], align: "left" },
    });

    // single memoized option object — no useState/effect double render
    const options = useMemo(() => ({
        backgroundColor: backgroundColor && backgroundColor !== '#000' ? backgroundColor : 'transparent',
        tooltip: { show: false, trigger: 'axis', axisPointer: { show: false } },
        legend: { data: [' '], show: false, textStyle: { color: "#ccc" } },
        toolbox: { left: 'center', itemSize: 18, top: '95%', feature: {} },
        dataZoom: [{
            type: 'slider',
            show: showZoomer,
            yAxisIndex: [0],
            zoomOnMouseWheel: true,
            moveOnMouseMove: true,
            moveOnMouseWheel: true,
            width: 16,
            start: startEndValue && startEndValue[0],
            end: startEndValue && startEndValue[1],
        }],
        title: {
            show: false,
            text: param,
            subtext: titleTooltip,
            subtextStyle: { color: fontColor },
            left: 'left',
            padding: [10, 0, 0, 30],
            itemGap: 0,
            top: parseInt(21 * (parseInt(height) / 880)),
            textStyle: { color: fontColor, fontSize: FONT_SIZE[fontSize] },
        },
        grid: { left: "0%", right: "0%", bottom: "5%", top: '10%', containLabel: true },
        xAxis: buildXAxis(),
        yAxis: buildYAxis(),
        series: buildSeries(),
    }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dataSeries, startEndValue, selectedTop, tops, fontColor, backgroundColor, fontSize, wellFontSize, labelFontSize, curveOverrides, showGrid, gridColor, showZoomer]
    );

    const getTopCoords = (uwi, ins, startEnd) => {
        if (!tops) return [uwi, null, index, startEnd];
        let res = {};
        Object.keys(tops).map(topName => {
            if (tops[topName] === null || tops[topName] === undefined) {
                res[topName] = null;
                return null;
            }
            res[topName] = parseFloat(ins.convertToPixel({ yAxisIndex: 0 }, tops[topName]));
            return null;
        });
        return [uwi, res, index, startEnd];
    };

    const handleZoom = async (e) => {
        zoomRef.current = [e.start, e.end, e.from];
        insRef && insRef.current && onTopCoordsChange(getTopCoords(uwi, insRef.current.getEchartsInstance(), [e.start, e.end, e.from]));
    };

    return (
        <div style={{ width: columnWidth, height: '50vh' }}>
            <ReactECharts
                option={options}
                notMerge={true}
                lazyUpdate={true}
                ref={insRef}
                opts={{ width: columnWidth, height: (height || 750) + 'px' }}
                width={columnWidth}
                onChartReady={ins => {
                    ins.group = syncDepth ? 'shale' : uwi;
                    onChartReady(getTopCoords(uwi, ins));
                }}
                onEvents={{ "datazoom": handleZoom }}
            />
        </div>
    );
};
export default PpWellMultiCurve;
